import { SelectOption } from "@veneer/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import UsersManagementService from "../../services/userManagementApi/usersManagement.service";
import { CellFormulaValue, CellHyperlinkValue, Workbook } from "exceljs";
import { CategoriesFundEnum } from "../../models/enum/userManagementApi/CategoriesFundEnum";
import { CategoriesEnum } from "../../models/enum/userManagementApi/CategoriesEnum";
import {
  IErrorUpdate,
  IManageUser,
} from "../../models/input/userApi/IManageUser";

export const useUsersManagementMassive = () => {
  type SelectValue = number | string | boolean;

  const [t] = useTranslation("global");
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [openModalCreateDone, setOpenModalCreateDone] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);

  const [actionTypeSelected, setActionTypeSelected] = useState<SelectValue[]>(
    []
  );
  const [disabledDownloadTemplate, setDisabledDownloadTemplate] =
    useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [buttonSaveDisabled, setButtonSaveDisabled] = useState(true);
  const [jsonUsers, setJsonUsers] = useState<IManageUser[]>([]);
  const [jsonErrorUsers, setJsonErrorUsers] = useState<IErrorUpdate[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [messageError, setErrorMessage] = useState<string>("");
  const [errorFilePicker, setErrorFilePicker] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalHelp, setOpenModalHelp] = useState(false);
  const [actionTypeIDFile, setActionTypeIDFile] = useState(0);

  const optionsTypeAction: SelectOption[] = [
    { value: 1, label: t("header.create") },
    { value: 2, label: t("header.update") },
  ];

  const enum actionsEnum {
    create = 1,
    update = 2,
  }

  const handleDownloadTemplate = () => {
    setLoading(true);
    UsersManagementService.getTempateUsersManagementMassive(
      Number(actionTypeSelected),
      Number(languageID.value)
    ).then((r: Blob | any) => {
      try {
        if (r?.statusCode === 401) {
          console.error("Error:", "Download failed");
        } else if (r instanceof Blob) {
          try {
            let fileName = "template_{action}_users_management_massive";

            switch (Number(actionTypeSelected)) {
              case 1:
                fileName = fileName.replace("{action}", "create");
                break;
              case 2:
                fileName = fileName.replace("{action}", "update");
                break;
              default:
                fileName = fileName.replace("{action}", "");
            }

            const url = window.URL.createObjectURL(r);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName + ".xlsx");
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
          } catch (error) {
            console.error("Error:", error);
          }
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
        setActionTypeSelected([]);
      }
    });
  };

  useEffect(() => {
    if (actionTypeSelected.length > 0) {
      setDisabledDownloadTemplate(false);
    } else {
      setDisabledDownloadTemplate(true);
    }
  }, [actionTypeSelected]);

  const handleCreateUsers = () => {
    setActionTypeIDFile(0);
    setOpenModalCreate(true);
    setFileName("");
    setErrorFilePicker(false);
    setButtonSaveDisabled(true);
  };

  const handleOnClickExit = () => {
    setFileName("");
    setOpenModalCreate(false);
    setButtonSaveDisabled(true);
  };

  const categorySelectedApplyForPrintFund = (
    categorySelected: number
  ): boolean => {
    return (
      categorySelected === CategoriesEnum.Supplies ||
      categorySelected === CategoriesEnum.Printing ||
      categorySelected === CategoriesEnum.DYP ||
      categorySelected === CategoriesEnum.PPS
    );
  };

  const categorySelectedApplyForPCFund = (
    categorySelected: number
  ): boolean => {
    return (
      categorySelected === CategoriesEnum.Computing ||
      categorySelected === CategoriesEnum.PPS
    );
  };

  const getCategoryFundID = (isPrint: boolean, isPC: boolean): number => {
    if (isPrint && isPC) {
      return CategoriesFundEnum.Both;
    } else if (isPrint) {
      return CategoriesFundEnum.Print;
    } else if (isPC) {
      return CategoriesFundEnum.PC;
    } else {
      return 0;
    }
  };

  const generateErrorExcel = async (errorRows: IErrorUpdate[]) => {
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet("Errores");

    // 🔹 Headers
    sheet.columns = [
      { header: "Fila", key: "row", width: 10 },
      { header: "UserID", key: "userID", width: 15 },
      { header: "Nombre", key: "name", width: 25 },
      { header: "Categoría", key: "category", width: 20 },
      { header: "Errores", key: "errors", width: 50 },
    ];

    //Agregar filas
    errorRows.forEach((item) => {
      sheet.addRow({
        row: item.row,
        userID: item.userID,
        name: item.name || "",
        category: item.category || "",
        errors: item.errors.join(" | "),
      });
    });

    //Estilo
    const headerRow = sheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "00B0F0" },
      };
      cell.alignment = { horizontal: "center" };
      cell.border = {
        top: { style: "thin", color: { argb: "000000" } },
        left: { style: "thin", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
        right: { style: "thin", color: { argb: "000000" } },
      };
      cell.font = { bold: true };
    });

    //Descargar archivo
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Errores_Carga.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleMexicoFile = async (file: File) => {
    try {
      setFileName(file.name);

      const reader = new FileReader();
      const fileType = file.name.split(".").pop();

      //Validación de tipo de archivo
      if (fileType?.toLowerCase() !== "xlsx") {
        setErrorMessage(t("header.messageXLSX"));
        setErrorFilePicker(true);
        return;
      }

      setLoadingModal(true);
      setErrorFilePicker(false);

      reader.onload = async (event) => {
        const data = event.target?.result as ArrayBuffer;
        const workbook = new Workbook();

        try {
          await workbook.xlsx.load(data);

          //Lectura de hojas del workbook
          const hiddenSheet = workbook.getWorksheet("Data");
          const advisorsSheet = workbook.getWorksheet("Advisors");
          const templateSheet = workbook.getWorksheet("Template");

          // Si alguna hoja requerida no existe, se detiene el proceso
          if (!hiddenSheet || !advisorsSheet || !templateSheet) {
            setErrorMessage(t("modal.errorLoadingFile"));
            setErrorFilePicker(true);
            setLoadingModal(false);
            return;
          }

          // ═══════════════════════════════════════════════════════════════════
          // HELPERS
          // ═══════════════════════════════════════════════════════════════════

          /**
           * Convierte el valor de una celda a string de forma segura.
           */
          const getSafeString = (cellValue: any): string => {
            if (!cellValue) return "";
            if (typeof cellValue === "object") return cellValue?.text ?? "";
            return cellValue.toString().trim();
          };

          // Normaliza ignorando mayúsculas/minúsculas
          const normalizeStrict = (value?: string) =>
            value?.trim().toLowerCase() ?? "";

          // Busca un valor en un mapa ignorando mayúsculas/minúsculas y acentos
          const findInMap = (
            map: Record<string, number>,
            value?: string
          ): number => {
            if (!value) return 0;
            return (
              Object.entries(map).find(
                ([key]) => normalizeStrict(key) === normalizeStrict(value)
              )?.[1] ?? 0
            );
          };

          /**
           * Determina si el Fondo HP es requerido u prohibido según el partner y fondo seleccionado.
           * Retorna: 'required' | 'forbidden' | 'optional'
           */
          const getFundHPRule = (
            partner: string,
            fund: string
          ): "required" | "forbidden" | "optional" => {
            if (partner === "intel") return "required"; // Co-Selling siempre requiere Fondo HP
            if (partner === "microsoft") return "forbidden"; // JMA y Jumpstart nunca llevan Fondo HP
            if (partner === "hp exclusivo") return "required"; // HP siempre requiere Fondo HP

            if (partner === "amd") {
              if (fund === "cmf-1") return "required"; // CMF-1 requiere Fondo HP
              if (fund === "cmf-2") return "forbidden"; // CMF-2 no permite Fondo HP
              if (fund === "mdf") return "forbidden"; // MDF no permite Fondo HP
            }

            return "optional";
          };

          /**
           * Construye un mapa { nombre → id } a partir de dos columnas de una hoja.
           * Se detiene cuando encuentra una celda vacía en cualquiera de las dos columnas.
           */
          const buildMap = (sheet: any, idCol: string, nameCol: string) => {
            const map: Record<string, number> = {};
            let row = 1;

            while (true) {
              const id = sheet.getCell(`${idCol}${row}`).value;
              const name = getSafeString(
                sheet.getCell(`${nameCol}${row}`).value
              );

              if (!id || !name) break;

              map[name.trim()] = Number(id);
              row++;
            }

            return map;
          };

          // MAPAS DE REFERENCIA (hoja "Data")
          // Cada mapa relaciona un nombre legible con su ID en la base de datos

          const rolesMap = buildMap(hiddenSheet, "A", "B"); // Roles de usuario
          const timesMap = buildMap(hiddenSheet, "D", "E"); // Tiempos / jornadas
          const categoriesMap = buildMap(hiddenSheet, "G", "H"); // Categorías
          const partnersMap = buildMap(hiddenSheet, "J", "K"); // Partners
          const fundsMap = buildMap(hiddenSheet, "M", "N"); // Fondos
          const fundsHPMap = buildMap(hiddenSheet, "P", "Q"); // Fondos HP
          const feePCMap = buildMap(hiddenSheet, "Y", "Z"); // Cuota PC
          const feePrintMap = buildMap(hiddenSheet, "AB", "AC"); // Cuota impresión
          const supervisorsMap = buildMap(hiddenSheet, "AK", "AL"); // Supervisores
          const citiesMap = buildMap(hiddenSheet, "AH", "AI"); // Ciudades

          const countriesMap: Record<string, number> = {};

          // MAPA DE ASESORES (hoja "Advisors")
          // Se indexa por UserID para acceso rápido desde la hoja Template

          const advisorsMap: Record<number, any> = {};

          let row = 2; // La fila 1 contiene encabezados
          while (advisorsSheet.getCell(`A${row}`).value) {
            const userID = Number(advisorsSheet.getCell(`A${row}`).value);
            const fullName = getSafeString(
              advisorsSheet.getCell(`B${row}`).value
            );

            const [name, ...rest] = fullName.split(" ");
            const lastName = rest.join(" ");

            advisorsMap[userID] = {
              Name: name,
              LastName: lastName,
              Document: getSafeString(advisorsSheet.getCell(`C${row}`).value),
              Country: getSafeString(advisorsSheet.getCell(`D${row}`).value),
              City: getSafeString(advisorsSheet.getCell(`F${row}`).value),
              Phone: getSafeString(advisorsSheet.getCell(`G${row}`).value),
              Email: getSafeString(advisorsSheet.getCell(`H${row}`).value),
              Supervisor: getSafeString(advisorsSheet.getCell(`I${row}`).value),
            };

            row++;
          }

          // ═══════════════════════════════════════════════════════════════════
          // PROCESAMIENTO DE LA HOJA "Template"
          // ═══════════════════════════════════════════════════════════════════
          const rows: IManageUser[] = []; // Filas válidas listas para guardar
          const rowsWithErrors: any[] = []; // Filas con errores para reportar

          templateSheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return; // Saltar encabezados

            // ── Lectura de celdas ─────────────────────────────────────────────
            const userID = Number(getValueCellTemplate(row.getCell(1).value));
            const advisor = advisorsMap[userID];

            const role = getValueCellTemplate(row.getCell(2).value)?.trim();
            const time = getValueCellTemplate(row.getCell(3).value)?.trim();
            const category = getValueCellTemplate(row.getCell(4).value)?.trim();

            // Campos de (Print)
            const partnerPrint = getValueCellTemplate(
              row.getCell(5).value
            )?.trim();
            const fundPrint = getValueCellTemplate(
              row.getCell(6).value
            )?.trim();
            const fundHPPrint = getValueCellTemplate(
              row.getCell(7).value
            )?.trim();

            // Campos de (PC)
            const partnerPC = getValueCellTemplate(
              row.getCell(8).value
            )?.trim();
            const fundPC = getValueCellTemplate(row.getCell(9).value)?.trim();
            const fundHPPC = getValueCellTemplate(
              row.getCell(10).value
            )?.trim();

            // Cuota
            const feePCLabel = getValueCellTemplate(
              row.getCell(11).value
            )?.trim();
            const feePrintLabel = getValueCellTemplate(
              row.getCell(12).value
            )?.trim();


            const roleID = findInMap(rolesMap, role);
            const timeID = findInMap(timesMap, time);
            const categoryID = findInMap(categoriesMap, category);

            // ── Normalización de valores para comparaciones ───────────────────
            // Se usa lowercase y trim para evitar errores por mayúsculas/espacios
            const normalize = (value?: string) =>
              value?.trim().toLowerCase() ?? "";

            const partnerPrintNormalized = normalize(partnerPrint);
            const fundPrintNormalized = normalize(fundPrint);
            const fundHPPrintNormalized = normalize(fundHPPrint);

            const partnerPCNormalized = normalize(partnerPC);
            const fundPCNormalized = normalize(fundPC);
            const fundHPPCNormalized = normalize(fundHPPC);

            const feePCNormalized = normalize(feePCLabel);
            const feePrintNormalized = normalize(feePrintLabel);

            // ── Flags de categoría ────────────────────────────────────────────
            // Determinan qué campos son obligatorios y qué reglas aplican
            const requirePrintFund =
              categorySelectedApplyForPrintFund(categoryID);
            const requirePCFund = categorySelectedApplyForPCFund(categoryID);

            const isSuppliesOrDYP =
              categoryID === CategoriesEnum.Supplies ||
              categoryID === CategoriesEnum.DYP;
            const isPrinting = categoryID === CategoriesEnum.Printing;
            const isComputing = categoryID === CategoriesEnum.Computing;
            const isPPS = categoryID === CategoriesEnum.PPS;

            // ── Helper: verifica si un valor existe en un mapa ──
            const existsInMap = (
              map: Record<string, number>,
              value?: string
            ) => {
              if (!value) return false;
              const normalized = value.trim().toLowerCase();
              return Object.keys(map).some(
                (key) => key.toLowerCase().trim() === normalized
              );
            };

            /**
             * Valida que el fondo (fund) sea compatible con el partner y la categoría.
             * Las reglas varían según si es Supplies/DYP, Printing, Computing o PPS.
             */
            const isValidFund = (partner: string, fund: string): boolean => {
              if (isSuppliesOrDYP) {
                return fund === "hp";
              }

              if (isPrinting || isComputing || isPPS) {
                if (partner === "intel") return fund === "co-selling";
                if (partner === "microsoft")
                  return fund === "jma" || fund === "jumpstart";
                if (partner === "amd")
                  return fund === "cmf-1" || fund === "cmf-2" || fund === "mdf";
                if (partner === "hp exclusivo") return fund === "hp";
                return false;
              }

              return true;
            };

            /**
             * Valida que el fondo HP sea compatible con el partner, la categoría
             * y el fondo principal seleccionado.
             */
            const isValidFundHP = (
              partner: string,
              fund: string,
              fundPrint: string
            ): boolean => {
              if (isSuppliesOrDYP) {
                return fund === "opex" || fund === "contra to opex";
              }

              if (isPrinting || isComputing || isPPS) {
                if (partner === "intel")
                  return fund === "opex" || fund === "contra to opex";
                if (partner === "microsoft") return false;

                if (partner === "amd") {
                  if (fundPrint === "cmf-1")
                    return fund === "opex" || fund === "contra to opex";
                  return false;
                }

                if (partner === "hp exclusivo")
                  return fund === "opex" || fund === "contra to opex";

                return false;
              }

              return true;
            };

            /**
             * Valida que el partner sea compatible con la categoría.
             * Para Supplies/DYP solo se permite "HP Exclusivo".
             */
            const isValidPartner = (partner: string): boolean => {
              if (isSuppliesOrDYP) return partner === "hp exclusivo";
              return true;
            };

            // ── Pre-validación de existencia en mapas ─────────────────────────
            const isPartnerValid = existsInMap(partnersMap, partnerPrint);
            const isFundValid = existsInMap(fundsMap, fundPrint);
            const isFundHPValid = existsInMap(fundsHPMap, fundHPPrint);
            const isPartnerPCValid = existsInMap(partnersMap, partnerPC);
            const isFundPCValid = existsInMap(fundsMap, fundPC);
            const isFundHPPCValid = existsInMap(fundsHPMap, fundHPPC);

            // ═══════════════════════════════════════════════════════════════════
            // VALIDACIONES (en orden: Rol → Tiempo → Categoría →
            //               Partner Print → Fondo Print → Fondo HP Print →
            //               Partner PC   → Fondo PC    → Fondo HP PC)
            // ═══════════════════════════════════════════════════════════════════
            const errors: string[] = [];

            // ── 1. Rol ────────────────────────────────────────────────────────
            if (!roleID) {
              errors.push("Role inválido");
            }

            // ── 2. Tiempo ─────────────────────────────────────────────────────
            if (!timeID) {
              errors.push("Time inválido");
            }

            // ── 3. Categoría ──────────────────────────────────────────────────
            if (!categoryID) {
              errors.push("Categoría inválida");
            }

            // ── 4. Usuario (asesor) ───────────────────────────────────────────
            if (!advisor) {
              errors.push("Usuario no encontrado");
            }

            // ── 5. Partner (impresión) ────────────────────────────────────────
            if (requirePrintFund) {
              if ((isSuppliesOrDYP || isPrinting || isPPS) && !partnerPrint) {
                errors.push("Partner Print requerido");
              }

              if (partnerPrint && !isPartnerValid) {
                errors.push("Partner Print inválido");
              }

              if (
                isSuppliesOrDYP &&
                partnerPrint &&
                partnerPrintNormalized !== "hp exclusivo"
              ) {
                errors.push("Partner Print inválido (solo HP Exclusivo)");
              }
            }

            // ── 6. Fondo (impresión) ──────────────────────────────────────────
            if (requirePrintFund) {
              if (partnerPrint && isPartnerValid && !isFundValid) {
                errors.push("Fund Print inválido");
              }

              if (
                isSuppliesOrDYP &&
                fundPrint &&
                fundPrintNormalized !== "hp"
              ) {
                errors.push("Fund Print inválido (solo HP)");
              }

              if (
                (isPrinting || isPPS) &&
                fundPrint &&
                isPartnerValid &&
                isFundValid &&
                !isValidFund(partnerPrintNormalized, fundPrintNormalized)
              ) {
                errors.push(`Fondo Print no permitido para ${partnerPrint}`);
              }
            }

            // ── 7. Fondo HP (impresión) ───────────────────────────────────────────
            if (requirePrintFund && isPartnerValid && isFundValid) {
              const fundHPRule = getFundHPRule(
                partnerPrintNormalized,
                fundPrintNormalized
              );
              const categoryLabel = isPrinting
                ? "Printing"
                : isPPS
                  ? "PPS"
                  : "Supplies/DYP";

              // Requerido y viene vacío
              if (fundHPRule === "required" && !fundHPPrint) {
                errors.push(`Fondo HP Print requerido para ${partnerPrint}`);
              }

              // Prohibido y viene con valor
              if (fundHPRule === "forbidden" && fundHPPrint) {
                errors.push(
                  `Fondo HP Print no permitido para ${partnerPrint} en ${categoryLabel}`
                );
              }

              // Viene con valor: validar existencia en mapa y combinación
              if (fundHPPrint) {
                if (!isFundHPValid) {
                  errors.push("Fund HP Print inválido");
                } else if (
                  !isValidFundHP(
                    partnerPrintNormalized,
                    fundHPPrintNormalized,
                    fundPrintNormalized
                  )
                ) {
                  errors.push(
                    `Fondo HP Print no permitido para ${partnerPrint} en ${categoryLabel}`
                  );
                }
              }
            }

            // ── 8. Partner (PC) ───────────────────────────────────────────────
            if (requirePCFund) {
              if ((isComputing || isPPS) && !partnerPC) {
                errors.push("Partner PC requerido");
              }

              if (partnerPC && !isPartnerPCValid) {
                errors.push("Partner PC inválido");
              }
            }

            // ── 9. Fondo (PC) ─────────────────────────────────────────────────
            if (requirePCFund) {
              if (fundPC && !existsInMap(fundsMap, fundPC)) {
                errors.push("Fund PC inválido");
              }

              if (
                (isComputing || isPPS) &&
                fundPC &&
                isPartnerPCValid &&
                isFundPCValid &&
                !isValidFund(partnerPCNormalized, fundPCNormalized)
              ) {
                errors.push("Fund PC inválido");
              }
            }

            // ── 10. Fondo HP (PC) ─────────────────────────────────────────────────
            if (requirePCFund && isPartnerPCValid && isFundPCValid) {
              const fundHPPCRule = getFundHPRule(
                partnerPCNormalized,
                fundPCNormalized
              );
              const categoryLabel = isComputing ? "Computing" : "PPS";

              // Requerido y viene vacío
              if (fundHPPCRule === "required" && !fundHPPC) {
                errors.push(`Fondo HP PC requerido para ${partnerPC}`);
              }

              // Prohibido y viene con valor
              if (fundHPPCRule === "forbidden" && fundHPPC) {
                errors.push(
                  `Fondo HP PC no permitido para ${partnerPC} en ${categoryLabel}`
                );
              }

              // Viene con valor: validar existencia en mapa y combinación
              if (fundHPPC) {
                if (!isFundHPPCValid) {
                  errors.push("Fund HP PC inválido");
                } else if (
                  !isValidFundHP(
                    partnerPCNormalized,
                    fundHPPCNormalized,
                    fundPCNormalized
                  )
                ) {
                  errors.push(
                    `Fondo HP PC no permitido para ${partnerPC} en ${categoryLabel}`
                  );
                }
              }
            }

            // ── 11. Cuota PC ──────────────────────────────────────────────────────
            if (!feePCLabel) {
              errors.push("Cuota PC requerida");
            } else if (feePCNormalized !== "si" && feePCNormalized !== "no") {
              errors.push("Cuota PC inválida (solo Si o No)");
            }

            // ── 12. Cuota Print ───────────────────────────────────────────────────
            if (!feePrintLabel) {
              errors.push("Cuota Print requerida");
            } else if (
              feePrintNormalized !== "si" &&
              feePrintNormalized !== "no"
            ) {
              errors.push("Cuota Print inválida (solo Si o No)");
            }

            // ── 13. Al menos una cuota debe ser Sí ───────────────────────────────
            // Solo aplica cuando ambas están presentes y son valores válidos
            if (
              feePCLabel &&
              feePrintLabel &&
              feePCNormalized === "no" &&
              feePrintNormalized === "no"
            ) {
              errors.push("Al menos una cuota (PC o Print) debe ser Sí");
            }

            // ── Si hay errores, se registra la fila y se omite ────────────────
            if (errors.length > 0) {
              rowsWithErrors.push({
                row: rowNumber,
                userID,
                name: advisor?.Name || "",
                category,
                errors,
              });
              return;
            }

            // ═══════════════════════════════════════════════════════════════════
            // CONSTRUCCIÓN DEL OBJETO DE SALIDA
            // Solo se llega aquí si la fila no tiene errores
            // ═══════════════════════════════════════════════════════════════════
            const rowData: IManageUser = {
              // Datos del asesor
              UserID: userID,
              Name: advisor.Name,
              LastName: advisor.LastName,
              NumberID: advisor.Document,
              CountryID: countriesMap[advisor.Country || ""] ?? 12,
              CityID: citiesMap[advisor.City || ""] ?? 0,
              Phone: advisor.Phone ? advisor.Phone.replace(/\D/g, "") : "",
              Email: advisor.Email,
              SupervisorID: supervisorsMap[advisor.Supervisor || ""] ?? 0,

              // Clasificación
              RoleID: roleID,
              TimeID: timeID,
              CategoryID: categoryID,

              // Partner y fondos de impresión
              PartnerPrintID:
                requirePrintFund &&
                partnerPrint &&
                (isSuppliesOrDYP
                  ? isValidPartner(partnerPrintNormalized)
                  : isPrinting || isPPS
                    ? true
                    : true)
                  ? (partnersMap[partnerPrint] ?? 0)
                  : 0,

              FundPrintID:
                requirePrintFund &&
                fundPrint &&
                (isSuppliesOrDYP
                  ? isValidFund(partnerPrintNormalized, fundPrintNormalized)
                  : isPrinting || isPPS
                    ? isValidFund(partnerPrintNormalized, fundPrintNormalized)
                    : true)
                  ? (fundsMap[fundPrint] ?? 0)
                  : 0,

              FundHpPrintID:
                requirePrintFund &&
                fundHPPrint &&
                (isSuppliesOrDYP
                  ? isValidFundHP(
                      partnerPrintNormalized,
                      fundHPPrintNormalized,
                      fundPrintNormalized
                    )
                  : isPrinting || isPPS
                    ? isValidFundHP(
                        partnerPrintNormalized,
                        fundHPPrintNormalized,
                        fundPrintNormalized
                      )
                    : true)
                  ? (fundsHPMap[fundHPPrint] ?? 0)
                  : 0,

              // Partner y fondos de PC
              PartnerPCID:
                requirePCFund &&
                partnerPC &&
                (isComputing || isPPS
                  ? isValidPartner(partnerPCNormalized)
                  : true)
                  ? (partnersMap[partnerPC] ?? 0)
                  : 0,

              FundPCID:
                requirePCFund &&
                fundPC &&
                (isComputing || isPPS
                  ? isValidFund(partnerPCNormalized, fundPCNormalized)
                  : true)
                  ? (fundsMap[fundPC] ?? 0)
                  : 0,

              FundHpPCID:
                requirePCFund &&
                fundHPPC &&
                (isComputing || isPPS
                  ? isValidFundHP(
                      partnerPCNormalized,
                      fundHPPCNormalized,
                      fundPCNormalized
                    )
                  : true)
                  ? (fundsHPMap[fundHPPC] ?? 0)
                  : 0,

              // Otros
              CategoryFundID: getCategoryFundID(
                requirePrintFund,
                requirePCFund
              ),
              FeePC: feePCMap[feePCLabel || ""] ?? 0,
              FeePrint: feePrintMap[feePrintLabel || ""] ?? 0,
            };

            rows.push(rowData);
          });

          setJsonErrorUsers(rowsWithErrors);
          setJsonUsers(rows);
        } catch (err) {
          console.log("Error handleMexicoFile", err);
          setErrorMessage(t("modal.errorLoadingFile"));
          setErrorFilePicker(true);
        } finally {
          setLoadingModal(false);
        }
      };

      reader.readAsArrayBuffer(file);
      setButtonSaveDisabled(false);
    } catch (err) {
      setErrorMessage(t("modal.errorLoadingFile"));
      setErrorFilePicker(true);
      setLoadingModal(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = e.target;
    if (!fileInput.files) {
      setErrorMessage(t("modal.errorLoadingFile"));
      setErrorFilePicker(true);
      return;
    }

    const file = fileInput.files[0];
    setFileName(file.name);
    const reader = new FileReader();
    const fileType = file.name.split(".").pop();

    if (fileType?.toLowerCase() !== "xlsx") {
      setErrorMessage(t("header.messageXLSX"));
      setErrorFilePicker(true);
      return;
    }

    setLoadingModal(true);
    setErrorFilePicker(false);
    reader.onload = async (event) => {
      const data = event.target?.result as ArrayBuffer;
      const workbook = new Workbook();

      try {
        await workbook.xlsx.load(data);
        const hiddenSheet = workbook.getWorksheet("Data");
        const advisorsSheet = workbook.getWorksheet("Advisors");
        var actionTypeID = 0; // 1 for create and 2 for update
        let actionTypeMexicoID = 0;
        var countriesMap: Record<string, number> = {};
        var citiesMap: Record<string, number> = {};
        var timesMap: Record<string, number> = {};
        var categoriesMap: Record<string, number> = {};
        var partnersMap: Record<string, number> = {};
        var fundsMap: Record<string, number> = {};
        var fundsHPMap: Record<string, number> = {};
        var supervisorsMap: Record<string, number> = {};
        var rolesMap: Record<string, number> = {};
        var advisorsMap: Record<string, number> = {};
        const rows: any[] = [];

        if (hiddenSheet) {
          actionTypeID = Number(hiddenSheet.getCell("U1").value);
          setActionTypeIDFile(actionTypeID);

          const countryID = Number(hiddenSheet.getCell("V1").value ?? 0);
          actionTypeMexicoID = Number(hiddenSheet.getCell("S1").value);

          setActionTypeIDFile(actionTypeID);

          if (countryID === 12 && actionTypeMexicoID === actionsEnum.update) {
            setActionTypeIDFile(actionTypeMexicoID);
            await handleMexicoFile(file);
            return;
          }
          // set map of data list
          // countries list
          let row = 1;
          while (
            hiddenSheet.getCell(`A${row}`).value &&
            hiddenSheet.getCell(`B${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`A${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`B${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !countriesMap[propertyName?.trim()]) {
              countriesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // cities list
          row = 1;
          while (
            hiddenSheet.getCell(`E${row}`).value &&
            hiddenSheet.getCell(`F${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`E${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`F${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !citiesMap[propertyName?.trim()]) {
              citiesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // times list
          row = 1;
          while (
            hiddenSheet.getCell(`G${row}`).value &&
            hiddenSheet.getCell(`H${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`G${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`H${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !timesMap[propertyName?.trim()]) {
              timesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // categories list
          row = 1;
          while (
            hiddenSheet.getCell(`I${row}`).value &&
            hiddenSheet.getCell(`J${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`I${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`J${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !categoriesMap[propertyName?.trim()]) {
              categoriesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // categories list
          row = 1;
          while (
            hiddenSheet.getCell(`I${row}`).value &&
            hiddenSheet.getCell(`J${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`I${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`J${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !categoriesMap[propertyName?.trim()]) {
              categoriesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // partners list
          row = 1;
          while (
            hiddenSheet.getCell(`K${row}`).value &&
            hiddenSheet.getCell(`L${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`K${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`L${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !partnersMap[propertyName?.trim()]) {
              partnersMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // funds list
          row = 1;
          while (
            hiddenSheet.getCell(`M${row}`).value &&
            hiddenSheet.getCell(`N${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`M${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`N${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !fundsMap[propertyName?.trim()]) {
              fundsMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // funds HP list
          row = 1;
          while (
            hiddenSheet.getCell(`O${row}`).value &&
            hiddenSheet.getCell(`P${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`O${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`P${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !fundsHPMap[propertyName?.trim()]) {
              fundsHPMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // supervisors list
          row = 1;
          while (
            hiddenSheet.getCell(`Q${row}`).value &&
            hiddenSheet.getCell(`R${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`Q${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`R${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !supervisorsMap[propertyName?.trim()]) {
              supervisorsMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // roles list
          row = 1;
          while (
            hiddenSheet.getCell(`S${row}`).value &&
            hiddenSheet.getCell(`T${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`S${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`T${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !rolesMap[propertyName?.trim()]) {
              rolesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }
        }

        if (advisorsSheet && actionTypeID === actionsEnum.update) {
          let row = 1;
          // set map of data list
          while (
            advisorsSheet.getCell(`A${row}`).value &&
            advisorsSheet.getCell(`B${row}`).value
          ) {
            const propertyID = Number(advisorsSheet.getCell(`A${row}`).value);
            const propertyName = advisorsSheet
              .getCell(`B${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !advisorsMap[propertyName?.trim()]) {
              advisorsMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }
        }

        if (!actionTypeID) {
          setErrorMessage(t("header.messageFormatError"));
          setErrorFilePicker(true);
          return;
        }

        const sheet = workbook.worksheets[0];
        sheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) {
            return;
          }

          let advisorName,
            name,
            lastName,
            document,
            country,
            city,
            phone,
            email,
            supervisor,
            role,
            time,
            category,
            partnerPrint,
            fundPrint,
            fundHPPrint,
            partnerPC,
            fundPC,
            fundHPPC,
            feePC,
            feePrint;

          let yesValue = t("header.yes");
          let feePCLabel = getValueCellTemplate(row.getCell(19).value);
          let feePCValue = feePCLabel === yesValue ? 1 : 0;

          let feePrintLabel = getValueCellTemplate(row.getCell(20).value);
          let feePrintValue = feePrintLabel === yesValue ? 1 : 0;

          if (actionTypeID === actionsEnum.create) {
            name = getValueCellTemplate(row.getCell(1).value);
            lastName = getValueCellTemplate(row.getCell(2).value);
            document = getValueCellTemplate(row.getCell(3).value);
            country = getValueCellTemplate(row.getCell(4).value);
            city = getValueCellTemplate(row.getCell(6).value);
            phone = getValueCellTemplate(row.getCell(7).value);
            email = getValueCellTemplate(row.getCell(8).value);
            supervisor = getValueCellTemplate(row.getCell(9).value);
            role = getValueCellTemplate(row.getCell(10).value);
            time = getValueCellTemplate(row.getCell(11).value);
            category = getValueCellTemplate(row.getCell(12).value);
            partnerPrint = getValueCellTemplate(row.getCell(13).value);
            fundPrint = getValueCellTemplate(row.getCell(14).value);
            fundHPPrint = getValueCellTemplate(row.getCell(15).value);
            partnerPC = getValueCellTemplate(row.getCell(16).value);
            fundPC = getValueCellTemplate(row.getCell(17).value);
            fundHPPC = getValueCellTemplate(row.getCell(18).value);
            feePC = feePCValue;
            feePrint = feePrintValue;
          }

          if (
            actionTypeID === actionsEnum.update &&
            !getValueCellTemplate(row.getCell(1).value)
          ) {
            return;
          }

          if (actionTypeID === actionsEnum.update) {
            yesValue = t("header.yes");
            feePCLabel = getValueCellTemplate(row.getCell(18).value);
            feePCValue = feePCLabel === yesValue ? 1 : 0;

            feePrintLabel = getValueCellTemplate(row.getCell(19).value);
            feePrintValue = feePrintLabel === yesValue ? 1 : 0;

            advisorName = getValueCellTemplate(row.getCell(1).value);
            document = getValueCellTemplate(row.getCell(2).value);
            country = getValueCellTemplate(row.getCell(3).value);
            city = getValueCellTemplate(row.getCell(5).value);
            phone = getValueCellTemplate(row.getCell(6).value);
            email = getValueCellTemplate(row.getCell(7).value);
            supervisor = getValueCellTemplate(row.getCell(8).value);
            role = getValueCellTemplate(row.getCell(9).value);
            time = getValueCellTemplate(row.getCell(10).value);
            category = getValueCellTemplate(row.getCell(11).value);
            partnerPrint = getValueCellTemplate(row.getCell(12).value);
            fundPrint = getValueCellTemplate(row.getCell(13).value);
            fundHPPrint = getValueCellTemplate(row.getCell(14).value);
            partnerPC = getValueCellTemplate(row.getCell(15).value);
            fundPC = getValueCellTemplate(row.getCell(16).value);
            fundHPPC = getValueCellTemplate(row.getCell(17).value);
            feePC = feePCValue;
            feePrint = feePrintValue;
          }

          const requirePrintFund = categorySelectedApplyForPrintFund(
            Number(categoriesMap[category || 0])
          );
          const requirePCFund = categorySelectedApplyForPCFund(
            Number(categoriesMap[category || 0])
          );

          if (
            ((actionTypeID === actionsEnum.update &&
              advisorName &&
              advisorsMap[advisorName.trim()]) ||
              actionTypeID === actionsEnum.create) &&
            ((actionTypeID === actionsEnum.create && name && lastName) ||
              actionTypeID === actionsEnum.update) &&
            document &&
            phone &&
            email &&
            country &&
            countriesMap[country] &&
            city &&
            citiesMap[city] &&
            supervisor &&
            supervisorsMap[supervisor] &&
            role &&
            rolesMap[role] &&
            time &&
            timesMap[time] &&
            category &&
            categoriesMap[category] &&
            ((requirePrintFund &&
              partnerPrint &&
              partnersMap[partnerPrint] &&
              fundPrint &&
              fundsMap[fundPrint]) ||
              !requirePrintFund) &&
            ((requirePCFund &&
              partnerPC &&
              partnersMap[partnerPC] &&
              fundPC &&
              fundsMap[fundPC]) ||
              !requirePCFund)
          ) {
            const categoryFundID = getCategoryFundID(
              requirePrintFund,
              requirePCFund
            );

            const rowData: IManageUser = {
              UserID: 0,
              Name: name,
              LastName: lastName,
              NumberID: document,
              CountryID: countriesMap[country],
              CityID: citiesMap[city],
              Phone: phone.replace(/\D/g, ""),
              Email: email,
              SupervisorID: supervisorsMap[supervisor],
              RoleID: rolesMap[role],
              TimeID: timesMap[time],
              CategoryID: categoriesMap[category],
              PartnerPrintID:
                partnerPrint && requirePrintFund
                  ? partnersMap[partnerPrint]
                  : 0,
              FundPrintID:
                fundPrint && requirePrintFund ? fundsMap[fundPrint] : 0,
              FundHpPrintID:
                fundHPPrint && requirePrintFund ? fundsHPMap[fundHPPrint] : 0,
              PartnerPCID:
                partnerPC && requirePCFund ? partnersMap[partnerPC] : 0,
              FundPCID: fundPC && requirePCFund ? fundsMap[fundPC] : 0,
              FundHpPCID: fundHPPC && requirePCFund ? fundsHPMap[fundHPPC] : 0,
              CategoryFundID: categoryFundID,
              FeePC: feePC ? Number(feePC) : 0,
              FeePrint: feePrint ? Number(feePrint) : 0,
            };

            if (actionTypeID === actionsEnum.update) {
              rowData.UserID = advisorName ? advisorsMap[advisorName] : 0;
            }

            rows.push(rowData);
          } else {
            setButtonSaveDisabled(true);
            setErrorFilePicker(true);
            setErrorMessage(
              t("modal.invalidDataUsersMassive").replace(
                "{0}",
                rowNumber.toString()
              )
            );
          }
        });
        setJsonUsers(rows);
      } catch (error) {
        setErrorFilePicker(true);
        setButtonSaveDisabled(true);
        setErrorMessage(t("modal.errorLoadingFile"));
      }

      setLoadingModal(false);
    };

    reader.readAsArrayBuffer(file);
    setButtonSaveDisabled(false);
  };

  const onClearFilePicker = () => {
    setFileName("");
    setErrorFilePicker(false);
    setButtonSaveDisabled(true);
  };

  const submitUsers = async () => {
    setLoadingModal(true);
    interface DataItem {
      userID: number;
      email?: string;
      numberID?: string;
      actionTypeID: number;
      result: number;
      message?: string;
    }

    try {
      const response: any[] = await UsersManagementService.manageUsersInBulk(
        jsonUsers,
        actionTypeIDFile
      );

      if (jsonErrorUsers.length > 0) {
        generateErrorExcel(jsonErrorUsers);
      }

      if (!response || response.length === 0) {
        setOpenModalCreate(false);
        setOpenModalError(true);
        return;
      }

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Datos");

      worksheet.columns = [
        { header: t("header.userId"), key: "userID", width: 15 },
        { header: t("header.email"), key: "email", width: 15 },
        { header: t("header.document"), key: "numberID", width: 15 },
        { header: t("modal.message"), key: "message", width: 30 },
      ];

      response.forEach((item: DataItem) => {
        if (item.actionTypeID === actionsEnum.create) {
          item.message =
            item.result === 1
              ? t("modal.userCreated")
              : t("modal.userNotCreated");
        }

        if (item.actionTypeID === actionsEnum.update) {
          item.message =
            item.result === 1
              ? t("modal.updateInfo")
              : t("modal.informationNotUpdated");
        }

        const row = worksheet.addRow(item);

        row.eachCell((cell) => {
          cell.alignment = { horizontal: "center" };
        });
      });

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "00B0F0" },
        };
        cell.alignment = { horizontal: "center" };
        cell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
        cell.font = { bold: true };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);

      setOpenModalCreate(false);
      setOpenModalCreateDone(true);

      const a = document.createElement("a");
      a.href = url;
      a.download = "users_result.xlsx";
      a.click();

      URL.revokeObjectURL(url);
      setLoadingModal(false);
    } catch (error) {
      setOpenModalCreate(false);
      setOpenModalError(true);
      setLoadingModal(false);
    }
  };

  const getValueCellTemplate = (cellValue: any): string | undefined => {
    if (typeof cellValue === "string" || typeof cellValue === "number") {
      return cellValue?.toString().trim();
    }

    if ((cellValue as CellHyperlinkValue)?.text) {
      return (cellValue as CellHyperlinkValue)?.text?.toString().trim();
    }

    if ((cellValue as CellFormulaValue)?.result) {
      return (cellValue as CellFormulaValue)?.result?.toString().trim();
    }

    return;
  };

  return {
    optionsTypeAction,
    actionTypeSelected,
    setActionTypeSelected,
    handleCreateUsers,
    setOpenModalHelp,
    openModalHelp,
    loading,
    disabledDownloadTemplate,
    handleDownloadTemplate,
    loadingModal,
    openModalCreate,
    handleOnClickExit,
    buttonSaveDisabled,
    handleFileChange,
    fileName,
    errorFilePicker,
    messageError,
    submitUsers,
    onClearFilePicker,
    openModalCreateDone,
    openModalError,
    setOpenModalCreateDone,
    setOpenModalError,
  };
};
